import { useTaskHierarchyBackground } from '../TaskListView/TaskHierarchy'
import React, { useEffect, useRef, useState } from 'react'
import { Animated, Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import Icon from '../Icon'
import styles, { colors } from '../styles/global'
import moment from 'moment'
import TasksHelper from '../TaskListView/Utils/TasksHelper'
import ContactsHelper from '../ContactsView/Utils/ContactsHelper'
import SocialText from '../UIControls/SocialText/SocialText'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import SharedHelper from '../../utils/SharedHelper'
import BacklinksTag from '../Tags/BacklinksTag'
import PrivacyTag from '../Tags/PrivacyTag'
import { FEED_NOTE_OBJECT_TYPE, FEED_PUBLIC_FOR_ALL } from '../Feeds/Utils/FeedsConstants'
import { dismissAllPopups } from '../../utils/HelperFunctions'
import ProjectHelper, { checkIfSelectedProject } from '../SettingsView/ProjectsSettings/ProjectHelper'
import Swipeable from 'react-native-gesture-handler/Swipeable'
import { getDateFormat, getTimeFormat } from '../UIComponents/FloatModals/DateFormatPickerModal'
import { setPrevScreen, setSelectedNavItem, setSelectedNote } from '../../redux/actions'
import { DV_TAB_NOTE_EDITOR } from '../../utils/TabNavigationConstants'
import NavigationService from '../../utils/NavigationService'
import SwipeNewTaskWrapper from './SwipeNewTaskWrapper'
import { exitsOpenModals, removeModal, RICH_CREATE_TASK_MODAL_ID, storeModal } from '../ModalsManager/modalsManager'
import { LINKED_OBJECT_TYPE_NOTE, getDvNoteTabLink } from '../../utils/LinkingHelper'
import useBacklinks from '../../hooks/useBacklinks'
import useSwipeCloseGuard from '../../hooks/useSwipeCloseGuard'
import { openNoteDV } from './NotesDV/EditorView/notesHelper'
import URLTrigger from '../../URLSystem/URLTrigger'
import { getTheme } from '../../Themes/Themes'
import { Themes } from '../RootView/Themes'
import LastEditionData from './LastEditionData'
import Avatar from '../Avatar'
import { resolveNoteOwner } from './NoteFilters/noteOwnerFilterHelper'
import ProjectMoveIndicator from '../UIComponents/ProjectMoveIndicator'

const NotesItem = ({ openEditModal, note, project, ignoreAccessGranted, inCommentPopup, onPress }) => {
    const loggedUser = useSelector(state => state.loggedUser)
    const showFloatPopup = useSelector(state => state.showFloatPopup)
    const selectedProjectIndex = useSelector(state => state.selectedProjectIndex)
    const dispatch = useDispatch()
    const isSticky = note.stickyData.days > 0 && checkIfSelectedProject(selectedProjectIndex)
    let hasTasks = false
    const trimmedPreview = note.preview.trim()
    const emptyNote = !hasTasks && trimmedPreview.length === 0
    // The owner of a note can be an assistant since AT-2194, so resolve against assistants
    // as well as project members. Selector form (rather than a bare store read) so the row
    // repaints once the assistants/users slices finish loading.
    const noteOwner = useSelector(() => resolveNoteOwner(project.id, note.userId), shallowEqual)
    const accessGranted = SharedHelper.accessGranted(loggedUser, project.id)
    const { backlinksTasksCount, backlinkTaskObject, backlinksNotesCount, backlinkNoteObject } = useBacklinks(
        project.id,
        LINKED_OBJECT_TYPE_NOTE,
        'linkedParentNotesIds',
        note.id
    )
    // AT-2449 — Swipeable can deliver its close callbacks the wrong way round
    // and leave the row permanently unopenable. See the hook.
    const { blockOpen, ...swipeCloseGuard } = useSwipeCloseGuard()
    const [panColor, setPanColor] = useState(new Animated.Value(0))
    const showNewTaskPopup = useRef(false)
    const isUnmountedRef = useRef(false)
    const rightSwipeTimeoutRef = useRef(null)
    const [renderFlag, setRenderFlag] = useState(false)
    const itemSwipe = useRef()
    const theme = getTheme(Themes, loggedUser.themeName, 'RootView.StickyItem')

    const contact =
        note.parentObject?.type === 'contacts'
            ? TasksHelper.getContactInProject(project.id, note.parentObject.id)
            : null
    const contactPhotoURL = contact ? ContactsHelper.getContactPhotoURL(contact, false) : null

    const projectBackgroundColor = useTaskHierarchyBackground()
    const restingBackgroundColor = inCommentPopup ? colors.Secondary200 : projectBackgroundColor
    const outputColors = [colors.UtilityYellow125, restingBackgroundColor, colors.UtilityGreen125]
    const backColor = panColor.interpolate({
        inputRange: [-100, 0, 100],
        outputRange: outputColors,
        extrapolate: 'clamp',
    })

    const backColorHighlight = panColor.interpolate({
        inputRange: [-100, 0, 100],
        outputRange: [colors.UtilityYellow125, note.hasStar, colors.UtilityGreen125],
        extrapolate: 'clamp',
    })
    const highlightColor = inCommentPopup || note.hasStar.toLowerCase() === '#ffffff' ? backColor : backColorHighlight
    const usesCommentPopupBackground = inCommentPopup

    useEffect(() => {
        return () => {
            isUnmountedRef.current = true
            if (rightSwipeTimeoutRef.current) {
                clearTimeout(rightSwipeTimeoutRef.current)
            }
            // Debug: track unmount to diagnose setState-after-unmount
            if (__DEV__) console.debug('NotesItem unmounted', { noteId: note.id })
        }
    }, [])

    const openObjectNote = () => {
        const url = getDvNoteTabLink(
            project.id,
            note.parentObject.id,
            note.parentObject.type === 'topics' ? 'chats' : note.parentObject.type
        )
        URLTrigger.processUrl(NavigationService, url)
    }

    const onOpenNoteDV = () => {
        if (showFloatPopup === 0 && !blockOpen && !exitsOpenModals()) {
            if (note.parentObject) {
                openObjectNote()
            } else {
                openNoteDV(project, note)
            }
        } else {
            dismissAllPopups(true, true, true)
        }
    }

    const onOpenEditModal = () => {
        if (showFloatPopup === 0 && openEditModal && !blockOpen && !exitsOpenModals()) {
            openEditModal()
        } else {
            dismissAllPopups(true, true, true)
        }
    }

    const forceRender = () => {
        setRenderFlag(!renderFlag)
    }

    // Swipeable calls these during its own render, so adopting its dragX node synchronously would
    // set state on this component while another one renders. dragX is stable per Swipeable
    // instance, so deferring the swap by a microtask adopts it just as reliably, one render later.
    const adoptPanColor = dragX => {
        if (panColor !== dragX) {
            Promise.resolve().then(() => setPanColor(dragX))
        }
    }

    const renderRightSwipe = (progress, dragX) => {
        adoptPanColor(dragX)

        return <View style={{ width: 150 }} />
    }

    const renderLeftSwipe = (progress, dragX) => {
        adoptPanColor(dragX)

        return <View style={{ width: 150 }} />
    }

    const onRightSwipe = () => {
        itemSwipe.current.close()
        rightSwipeTimeoutRef.current = setTimeout(() => {
            storeModal(RICH_CREATE_TASK_MODAL_ID)
            showNewTaskPopup.current = true
        })
    }

    const onLeftSwipe = () => {
        itemSwipe.current.close()
        if (note.parentObject) {
            openObjectNote()
        } else {
            dispatch([setSelectedNavItem(DV_TAB_NOTE_EDITOR), setPrevScreen('NotesView'), setSelectedNote(note)])
            NavigationService.navigate('NotesDetailedView', {
                noteId: note.id,
                projectId: project.id,
            })
        }
    }

    const cancelPopover = () => {
        removeModal(RICH_CREATE_TASK_MODAL_ID)
        showNewTaskPopup.current = false
        if (isUnmountedRef.current) {
            console.warn('NotesItem.cancelPopover called after unmount', { noteId: note.id })
            return
        }
        forceRender()
    }

    const renderIcon = () => {
        if (note.parentObject) {
            switch (note.parentObject.type) {
                case 'tasks':
                    return 'check-square'
                case 'goals':
                    return 'target'
                case 'users':
                    return 'users'
                case 'contacts':
                    return 'user-aster'
                case 'topics':
                    return 'comments-thread'
                case 'skills':
                    return 'star'
                case 'assistants':
                    return 'cpu'
            }
        } else return 'file-text'
    }

    const backlinksCount = backlinksTasksCount + backlinksNotesCount
    const backlinkObject =
        backlinksCount === 1 ? (backlinksTasksCount === 1 ? backlinkTaskObject : backlinkNoteObject) : null

    const loggedUserIsCreator = loggedUser.uid === note.creatorId
    const loggedUserCanUpdateObject =
        !note.linkedToTemplate &&
        (loggedUserIsCreator || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(project.id))

    return (
        <View dataSet={{ annaObjectType: 'note', annaObjectId: note.id, annaProjectId: project.id }}>
            <View style={localStyles.swipeContainer}>
                <View style={localStyles.leftSwipeArea}>
                    <Icon name={'circle-details'} size={18} color={colors.UtilityGreen200} />
                    <View style={{ marginLeft: 4 }}>
                        <Text style={[styles.subtitle2, { color: colors.UtilityGreen200 }]}>Details</Text>
                    </View>
                </View>

                <View style={localStyles.rightSwipeArea}>
                    <View style={localStyles.rightSwipeAreaContainer}>
                        <Icon name={'check-square'} size={18} color={colors.UtilityYellow200} />
                        <View style={{ marginLeft: 4 }}>
                            <Text style={[styles.subtitle2, { color: colors.UtilityYellow200 }]}>Add task</Text>
                        </View>
                    </View>
                </View>
            </View>

            <Swipeable
                useNativeAnimations={false}
                ref={itemSwipe}
                rightThreshold={80}
                leftThreshold={80}
                enabled={!inCommentPopup}
                renderLeftActions={renderLeftSwipe}
                renderRightActions={renderRightSwipe}
                onSwipeableLeftWillOpen={onLeftSwipe}
                onSwipeableRightWillOpen={onRightSwipe}
                overshootLeft={false}
                overshootRight={false}
                friction={2}
                containerStyle={{ overflow: 'visible' }}
                failOffsetY={[-5, 5]}
                {...swipeCloseGuard}
            >
                <Animated.View
                    style={[
                        localStyles.container,
                        { backgroundColor: highlightColor },
                        isSticky && [localStyles.containerSticky, theme.containerSticky(project.color)],
                    ]}
                >
                    <ProjectMoveIndicator object={note} />
                    <TouchableOpacity
                        style={localStyles.subContainer}
                        onPress={inCommentPopup ? onPress : onOpenNoteDV}
                        disabled={!inCommentPopup && (blockOpen || ignoreAccessGranted ? false : !accessGranted)}
                        activeOpacity={blockOpen ? 1 : 0.5}
                        accessible={false}
                    >
                        <View style={{ flexDirection: 'column', width: '100%' }}>
                            <View style={localStyles.titleContainer}>
                                <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
                                    <TouchableOpacity
                                        onPress={inCommentPopup ? onPress : onOpenEditModal}
                                        disabled={
                                            !inCommentPopup &&
                                            (blockOpen || ignoreAccessGranted ? false : !accessGranted)
                                        }
                                        activeOpacity={blockOpen ? 1 : 0.5}
                                        accessible={false}
                                    >
                                        {isSticky ? (
                                            <Icon name="sticky-note" size={24} color={colors.Text03} />
                                        ) : contactPhotoURL ? (
                                            <Image
                                                source={{ uri: contactPhotoURL }}
                                                style={{ width: 24, height: 24, borderRadius: 100 }}
                                            />
                                        ) : (
                                            <Icon name={renderIcon()} size={24} color={colors.Text03} />
                                        )}
                                    </TouchableOpacity>
                                    <View style={{ flex: 1, minWidth: 0, overflow: 'hidden', marginHorizontal: 12 }}>
                                        <SocialText
                                            style={[
                                                styles.body1,
                                                {
                                                    color: usesCommentPopupBackground
                                                        ? colors.UtilityBlue100
                                                        : colors.Text01,
                                                },
                                            ]}
                                            numberOfLines={1}
                                            showEllipsis={true}
                                            task={{ linkBack: '' }}
                                            inTaskDetailedView={false}
                                            projectId={project.id}
                                            hasStar={note.hasStar}
                                            bgColor={
                                                note.hasStar.toLowerCase() !== '#ffffff'
                                                    ? backColorHighlight
                                                    : backColor
                                            }
                                        >
                                            {note !== undefined && (note.title != null || note.extendedTitle != null)
                                                ? note.extendedTitle != null && note.extendedTitle !== ''
                                                    ? note.extendedTitle
                                                    : note.title
                                                : ''}
                                        </SocialText>
                                    </View>
                                    <View style={{ flexDirection: 'row', marginLeft: 'auto' }}>
                                        {note.isPrivate && (
                                            <PrivacyTag
                                                projectId={project.id}
                                                object={note}
                                                objectType={FEED_NOTE_OBJECT_TYPE}
                                                disabled={
                                                    !accessGranted || note.parentObject || !loggedUserCanUpdateObject
                                                }
                                                style={{ marginLeft: 8 }}
                                            />
                                        )}
                                        {backlinksCount > 0 && (
                                            <BacklinksTag
                                                object={note}
                                                objectType={LINKED_OBJECT_TYPE_NOTE}
                                                projectId={project.id}
                                                style={{ marginLeft: 8 }}
                                                disabled={!accessGranted}
                                                backlinksCount={backlinksCount}
                                                backlinkObject={backlinkObject}
                                            />
                                        )}
                                        <View style={localStyles.userImageContainer}>
                                            <Avatar
                                                avatarId={note.userId}
                                                reviewerPhotoURL={noteOwner?.photoURL}
                                                size={20}
                                                borderSize={2}
                                            />
                                        </View>
                                    </View>
                                </View>
                            </View>
                            <View style={localStyles.notePreview}>
                                <View style={{ flex: 1, marginLeft: 8 }}>
                                    <SocialText
                                        style={[
                                            localStyles.description,
                                            usesCommentPopupBackground && localStyles.textInCommentPopup,
                                        ]}
                                        normalStyle={{ whiteSpace: 'normal' }}
                                        numberOfLines={1}
                                        projectId={project.id}
                                        hasStar={note.hasStar}
                                        bgColor={
                                            note.hasStar.toLowerCase() !== '#ffffff' ? backColorHighlight : backColor
                                        }
                                        inFeedComment={true}
                                        showEllipsis={true}
                                    >
                                        {emptyNote
                                            ? 'This note has no content'
                                            : !note.isPublicFor.includes(FEED_PUBLIC_FOR_ALL)
                                              ? 'Private note'
                                              : note.preview}
                                    </SocialText>
                                </View>
                            </View>

                            <LastEditionData
                                note={note}
                                projectId={project.id}
                                inCommentPopup={usesCommentPopupBackground}
                            />
                        </View>
                    </TouchableOpacity>
                </Animated.View>
            </Swipeable>

            <SwipeNewTaskWrapper
                projectId={project.id}
                objectId={note.id}
                sourceType={FEED_NOTE_OBJECT_TYPE}
                showPopup={showNewTaskPopup.current}
                cancelPopover={cancelPopover}
            />
        </View>
    )
}

const parseDate = date => {
    if (Date.now() - date < 60) {
        return 'Just now'
    }

    return `Edited: ${moment(date).format(`${getTimeFormat(true)} of ${getDateFormat()}`)}`
}

const localStyles = StyleSheet.create({
    container: {
        maxHeight: 92,
        flexDirection: 'row',
        justifyContent: 'space-between',
        flex: 1,
        paddingTop: 8,
        paddingBottom: 8,
        borderRadius: 4,
        marginHorizontal: -8,
        paddingHorizontal: 8,
        overflow: 'hidden',
    },
    subContainer: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        overflow: 'hidden',
    },
    containerSticky: {
        borderRadius: 4,
        paddingHorizontal: 8,
        marginLeft: -8,
        marginRight: -8,
    },
    titleContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    notePreview: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginLeft: 28,
        maxHeight: 32,
        paddingTop: 4,
        paddingBottom: 6,
    },
    description: {
        ...styles.body2,
        color: colors.Text02,
        display: 'flex',
        whiteSpace: 'normal',
    },
    textInCommentPopup: {
        color: colors.UtilityBlue125,
    },
    dateAndSubHint: {
        flex: 1,
        marginLeft: 36,
        maxHeight: 20,
        paddingBottom: 6,
        flexDirection: 'row',
        alignItems: 'flex-start',
        overflow: 'hidden',
    },
    subHintText: {
        color: colors.Text03,
        alignItems: 'flex-start',
    },
    userImageContainer: {
        marginLeft: 8,
    },
    swipeContainer: {
        height: '100%',
        width: '100%',
        borderRadius: 4,
        position: 'absolute',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    leftSwipeArea: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '50%',
        height: '100%',
        backgroundColor: colors.UtilityGreen100,
        borderRadius: 4,
        paddingLeft: 12,
    },
    rightSwipeAreaContainer: {
        marginLeft: 'auto',
        flexDirection: 'row',
        alignItems: 'center',
    },
    rightSwipeArea: {
        flexDirection: 'row',
        width: '50%',
        height: '100%',
        backgroundColor: colors.UtilityYellow100,
        borderRadius: 4,
        paddingRight: 12,
    },
})

export default NotesItem
